import { RequireProjectAdmin } from '@front/components/auth/RequireProjectAdmin';
import { useSourceSystem } from '@front/hook/Api/SourceSystem';
import { useNavigateBack } from '@front/hook/useNavigateBack';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFrame,
  DialogHeader,
  Loader,
  Textarea,
  TextField,
  useToast,
} from '@plaidev/sour-react';
import { createFileRoute, type ErrorComponentProps, useNavigate } from '@tanstack/react-router';
import { useProject } from 'auth-lib-front';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { DomainV2 } from 'troppus-common/domain';

import SourceSystem = DomainV2.Import.SourceSystem;

export const Route = createFileRoute('/settings/source-systems/$id')({
  component: () => (
    <RequireProjectAdmin>
      <SourceSystemDetailPage />
    </RequireProjectAdmin>
  ),
  errorComponent: SourceSystemDetailError,
});

function SourceSystemDetailError({ error }: ErrorComponentProps) {
  const message = error instanceof Error ? error.message : 'データの取得に失敗しました';
  return <div className="rounded-lg border border-negative bg-negative-light p-4 text-sm text-negative">{message}</div>;
}

type FormValues = {
  displayName: string;
  description: string;
};

/**
 * @feature ソースシステム詳細・編集
 * @capabilities 表示名・説明の編集, 削除
 * @behavior 表示名（必須）・説明を編集→「保存」で更新, 「削除」→確認ダイアログ→削除して一覧へ戻る。プロジェクト管理者専用
 * @airule IMPORTANT: Update this JSDoc when modifying this component's functionality
 */
function SourceSystemDetailPage() {
  const navigate = useNavigate();
  const goBackWithFallback = useNavigateBack();
  const toast = useToast();
  const { project } = useProject();
  const { id: idParam } = Route.useParams();

  const sourceSystemId = useMemo(() => SourceSystem.Id.FromString(idParam), [idParam]);

  const { data, isPending } = useSourceSystem.Get({ projectId: project.id, id: sourceSystemId });
  const updateMutation = useSourceSystem.Update();
  const deleteMutation = useSourceSystem.Delete();

  const [deleteOpen, setDeleteOpen] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isValid },
  } = useForm<FormValues>({
    defaultValues: { displayName: '', description: '' },
    mode: 'onChange',
  });

  useEffect(() => {
    if (!data) return;
    reset({ displayName: data.displayName, description: data.description ?? '' });
  }, [data, reset]);

  const onSubmit = handleSubmit(async (values) => {
    if (!data) return;
    const trimmedName = values.displayName.trim();
    const trimmedDescription = values.description.trim();
    try {
      await updateMutation.mutateAsync({
        projectId: project.id,
        id: sourceSystemId,
        updateProps: {
          displayName: trimmedName,
          description: trimmedDescription === '' ? undefined : trimmedDescription,
        },
      });
      reset({ displayName: trimmedName, description: trimmedDescription });
      toast.success('保存しました');
    } catch (err) {
      const message = err instanceof Error ? err.message : '更新に失敗しました';
      toast.error(message);
    }
  });

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync({ projectId: project.id, id: sourceSystemId });
      toast.success('削除しました');
      setDeleteOpen(false);
      void navigate({ to: '/settings/source-systems' });
    } catch (err) {
      const message = err instanceof Error ? err.message : '削除に失敗しました';
      toast.error(message);
    }
  };

  if (isPending) {
    return (
      <div className="flex justify-center py-16">
        <Loader />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted-light">ソースシステムが見つかりません。</p>
        <Button variant="quiet" size="sm" onClick={() => goBackWithFallback({ to: '/settings/source-systems' })}>
          一覧へ戻る
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-lg flex-col gap-8">
      <div className="flex flex-wrap items-center gap-4">
        <Button variant="basic" size="sm" onClick={() => goBackWithFallback({ to: '/settings/source-systems' })}>
          戻る
        </Button>
        <h1 className="text-xl font-semibold text-default">{data.displayName}</h1>
      </div>

      <form className="flex flex-col gap-6" onSubmit={(e) => void onSubmit(e)}>
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-default">
            表示名<span className="text-negative">*</span>
          </span>
          <Controller
            control={control}
            name="displayName"
            rules={{ validate: (v) => v.trim().length > 0 || '表示名を入力してください' }}
            render={({ field }) => <TextField variant="contained" value={field.value} onChange={field.onChange} required />}
          />
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-default">説明</span>
          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <Textarea className="[&>textarea]:w-full" value={field.value} onChange={field.onChange} rows={4} placeholder="説明を入力" />
            )}
          />
        </div>
        <Checkbox checked={data.isActive} disabled label="状態（APIでは変更不可）" />

        <div className="flex flex-wrap gap-3">
          <Button type="submit" variant="primary" loading={updateMutation.isPending} disabled={!isDirty || !isValid}>
            保存
          </Button>
          <Button type="button" variant="negative" onClick={() => setDeleteOpen(true)} disabled={deleteMutation.isPending}>
            削除
          </Button>
        </div>
      </form>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogFrame>
            <DialogHeader title="ソースシステムを削除" />
            <DialogBody>
              <p className="text-sm text-default">「{data.displayName}」を削除します。この操作は取り消せません。よろしいですか？</p>
            </DialogBody>
            <DialogActions>
              <DialogClose>
                <Button variant="basic">キャンセル</Button>
              </DialogClose>
              <Button variant="negative" loading={deleteMutation.isPending} onClick={() => void handleDelete()}>
                削除する
              </Button>
            </DialogActions>
          </DialogFrame>
        </DialogContent>
      </Dialog>
    </div>
  );
}
